<template>
  <div class="form-pages">
    <div class="pages-container">
      <!-- Regular pages -->
      <draggable
        v-model="form.pages"
        @end="onDragEnd"
        item-key="id"
        :animation="200"
        class="pages-list"
      >
        <template #item="{ element, index }">
          <div
            :key="element.id"
            class="page-item"
            :class="{ ['page-item--active']: activePageIndex === index }"
            @click="setActivePage(index)"
          >
            <span class="page-item__title">{{ element.title || `Page ${index + 1}` }}</span>
            <div class="page-item__actions">
              <Btn
                @click.stop="editPageSettings(index)"
                variant="plain"
                icon="edit"
                class="page-item__action"
                v-if="activePageIndex === index"
              />
            </div>
          </div>
        </template>
      </draggable>

      <!-- Separator -->
      <div class="pages-separator"></div>

      <!-- Ending pages -->
      <draggable
        v-model="form.ending_pages"
        @end="onDragEnd"
        item-key="id"
        :animation="200"
        class="pages-list"
      >
        <template #item="{ element, index }">
          <div
            :key="element.id"
            class="page-item"
            :class="{
              ['page-item--active']: activeEndingPageIndex === index,
              ['page-item--ending']: true,
            }"
            @click="setActiveEndingPage(index)"
          >
            <BaseIcon name="check-circle" size="16" />
            <span class="page-item__title">{{ element.title || `Ending ${index + 1}` }}</span>
            <div class="page-item__actions">
              <Btn
                @click.stop="editEndingPageSettings(index)"
                variant="plain"
                icon="edit"
                class="page-item__action"
              />
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <SplitBtn
      text="Add page"
      variant="secondary"
      class="add-page-btn"
      @click="addPage"
      @menu-item-click="handleMenuItemClick"
      :menu-items="[
        { label: 'Regular page', value: 'regular' },
        { label: 'Ending page', value: 'ending' },
      ]"
      direction="top-left"
    />
  </div>
</template>

<script>
  import { computed } from 'vue';
  import { useFormStore } from '@/store/formStore';
  import { useUIStore } from '@/store/uiStore';
  import draggable from 'vuedraggable';
  import Btn from '@/components/partials/Btn.vue';
  import SplitBtn from '@/components/partials/SplitBtn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import PageSettingsModalForm from '@/components/editor/settings/PageSettingsModalForm.vue';
  import EndingPageSettingsModalForm from '@/components/editor/settings/EndingPageSettingsModalForm.vue';

  export default {
    name: 'FormPages',
    components: {
      Btn,
      SplitBtn,
      draggable,
      BaseIcon,
    },
    props: {
      activePageIndex: {
        type: Number,
      },
      activeEndingPageIndex: {
        type: Number,
      },
    },
    emits: ['addPage', 'removePage', 'editPageSettings', 'setActivePage', 'setActiveEndingPage'],
    setup(props, { emit }) {
      const formStore = useFormStore();
      const uiStore = useUIStore();

      const form = computed(() => {
        // Ensure ending_pages exists
        if (!formStore.currentForm.ending_pages) {
          formStore.currentForm.ending_pages = [];
        }
        return formStore.currentForm;
      });

      const addPage = async () => {
        const response = await formStore.addPage();
        if (response.status === 'success') {
          formStore.setActivePage(form.value.pages.length - 1);
          editPageSettings(form.value.pages.length - 1);
        } else {
          console.error('Failed to add page:', response.error);
          alert('Failed to add page');
        }
      };

      const addEndingPage = async () => {
        const response = await formStore.addEndingPage();
        if (response.status === 'success') {
          const index = form.value.ending_pages.length - 1;
          formStore.setActiveEndingPage(index);
        } else {
          console.error('Failed to add ending page:', response.error);
          alert('Failed to add ending page');
        }
      };

      const editPageSettings = (index) => {
        const modalId = uiStore.addModal(PageSettingsModalForm, {
          title: 'Edit page settings',
          pageIndex: index,
          onSubmit: async () => {
            uiStore.closeModal(modalId);
          },
          onError: (errors) => {
            console.error('Form submission errors:', errors);
          },
        });
      };

      const editEndingPageSettings = (index) => {
        const modalId = uiStore.addModal(EndingPageSettingsModalForm, {
          title: 'Edit ending page settings',
          pageIndex: index,
          onSubmit: async () => {
            uiStore.closeModal(modalId);
          },
          onError: (errors) => {
            console.error('Form submission errors:', errors);
          },
        });
      };

      const setActivePage = (index) => {
        formStore.setActivePage(index);
      };

      const setActiveEndingPage = (index) => {
        formStore.setActiveEndingPage(index);
      };

      const onDragEnd = async () => {
        await formStore.saveCurrentForm();
      };

      const handleMenuItemClick = (value) => {
        if (value === 'regular') {
          addPage();
        } else if (value === 'ending') {
          addEndingPage();
        }
      };

      return {
        form,
        addPage,
        addEndingPage,
        editPageSettings,
        editEndingPageSettings,
        onDragEnd,
        setActivePage,
        setActiveEndingPage,
        handleMenuItemClick,
      };
    },
  };
</script>

<style scoped lang="scss">
  @import '@/assets/scss/base/mixins';

  .form-pages {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem;
    border-bottom: 1px solid var(--slate-200);
    gap: 1rem;

    &__section {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__header {
      padding: 0 0.5rem;
    }

    &__title {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-700);
      margin: 0;
    }

    .pages-container {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.5rem;
      overflow-x: auto;
    }

    .pages-list {
      display: flex;
      gap: 0.25rem;
      min-height: 2.5rem;
      flex-wrap: nowrap;
    }

    .pages-separator {
      width: 1px;
      align-self: stretch;
      background-color: var(--slate-200);
      margin: 0 0.5rem;
    }

    .page-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.3125rem 0.5rem;
      border-radius: 0.5rem;
      border: 1px solid var(--slate-200);
      background: var(--white);
      color: var(--slate-500);
      white-space: nowrap;
      @include fontSize('sm');

      &--ending {
        border-style: dashed;
      }

      &--active {
        background-color: var(--slate-100);
        border-color: var(--slate-300);
        color: var(--slate-700);
      }

      @include hover {
        cursor: pointer;
        background-color: var(--slate-50);
      }

      &__title {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      &__actions {
        display: flex;
        gap: 0.25rem;
      }
    }

    .add-page-btn {
      margin-left: 0.5rem;
    }
  }
</style>
